import React, { useState, useEffect, useRef } from "react";

import Utils from "../../utils/Utils";
import TableCellRegressionDiff from "../table/TableCellRegressionDiff";
import TableSkeleton from "../table/TableSkeleton";
import Checkbox from "../form/Checkbox";
import { AiOutlineArrowDown, AiOutlineArrowUp } from "react-icons/ai";

const tableHeader = [
  {
    title: "Category",
    showImage: false,
    sortable: false,
    key: "Category",
  },
  {
    title: "Offensive",
    showImage: false,
    sortable: false,
    key: "Offensive_Regression",
  },
  {
    title: "Defense",
    showImage: false,
    sortable: false,
    key: "Defense_Regression",
  },
];

const regressionAnalysisCategories = [
  {
    key: "Finishing at the Rim",
    label: "Finishing at the Rim",
    keyDiff: "Finishing at the Rim Diff",
    keyDiffRank: "Finishing at the Rim Diff rank",
    keyFG: "Finishing at the Rim FG%",
    keySQ: "Finishing at the Rim SQ FG%",
  },
  {
    key: "3PT",
    label: "3PT",
    keyDiff: "3PT Diff",
    keyDiffRank: "3PT Diff rank",
    keyFG: "3PT FG%",
    keySQ: "3PT SQ FG%",
  },
  {
    key: "Midrange",
    label: "Midrange",
    keyDiff: "Midrange Diff",
    keyDiffRank: "Midrange Diff rank",
    keyFG: "Midrange FG%",
    keySQ: "Midrange SQ FG%",
  },
  {
    key: "Post-Up",
    label: "Post-Up",
    keyDiff: "Post-Up Diff",
    keyDiffRank: "Post-Up Diff rank",
    keyFG: "Post-Up FG%",
    keySQ: "Post-Up SQ FG%",
  },
  {
    key: "Free Throws",
    label: "Free Throws",
    keyDiff: "Free Throws Diff",
    keyDiffRank: "Free Throws Diff rank",
    keyFG: "Free Throw FG%",
    keySQ: "Free Throw SQ FG%",
  },
];

const TeamRegressionAnalysis = ({
  regressionDEF = {},
  regressionOFF = {},
  regressionEFF = {},
  confRegressionOFF = {},
  confRegressionDEF = {},
  leagueType,
  year,
  conferenceTeamCount = 0,
  loading = false,
}) => {
  const checkBoxRef = useRef(null);
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  const [activeRegressionType, setActiveRegressionType] = useState({
    DEF: regressionDEF,
    OFF: regressionOFF,
    EFF: regressionEFF,
  });
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);

  useEffect(() => {
    if (isCheckboxChecked) {
      setCheckboxChecked(true);
      setActiveRegressionType({
        OFF: confRegressionOFF,
        DEF: confRegressionDEF,
        EFF: regressionEFF,
      });
    } else {
      setCheckboxChecked(false);

      setActiveRegressionType({
        OFF: regressionOFF,
        DEF: regressionDEF,
        EFF: regressionEFF,
      });
    }
  }, [isCheckboxChecked]);

  useEffect(() => {
    if (checkboxChecked) {
      setActiveRegressionType({
        OFF: confRegressionOFF,
        DEF: confRegressionDEF,
        EFF: regressionEFF,
      });
    } else {
      setActiveRegressionType({
        OFF: regressionOFF,
        DEF: regressionDEF,
        EFF: regressionEFF,
      });
    }
  }, [confRegressionOFF, confRegressionDEF, regressionEFF, regressionOFF, regressionDEF, year]);

  const handleCheckboxClick = () => {
    return checkBoxRef.current.checked ? setIsCheckboxChecked(true) : setIsCheckboxChecked(false);
  };

  const rankConfig = {
    leagueType: leagueType,
    conferenceTeamCount: 0,
    hideGradients: true,
    hideRank: true,
  };

  return loading ? (
    <TableSkeleton tableHeader={tableHeader} tableRowsCount={5} />
  ) : (
    <div className="row no-gutters">
      <div className="col-8 text-left text-md-left my-2 my-mb-3 mt-mb-0 d-flex align-items-center">
        <h4 style={{ fontSize: 36, fontFamily: "var(--font-oswald)" }}>Regression Analysis</h4>
      </div>
      {leagueType !== "wnba" && (
        <div className="col-4 text-center text-sm-right text-md-right my-2 my-mb-3 mt-mb-0 d-flex justify-content-end">
          <Checkbox
            id="regressionConferenceOnlyCheck"
            ref={checkBoxRef}
            onClick={handleCheckboxClick}
            label="Conference Only"
            checked={checkBoxRef?.current?.checked}
          />
        </div>
      )}
      {loading === false &&
      Object.keys(activeRegressionType["OFF"])?.length > 0 &&
      Object.keys(activeRegressionType["DEF"])?.length > 0 ? (
        <div className="col-12">
          <div className="table-responsive p-0 ptY-0">
            <table className="table regressionAnalysis">
              <thead style={{ top: "0px" }}>
                <tr>
                  <th
                    rowSpan="2"
                    style={{
                      borderTopLeftRadius: "24px",
                      borderRight: "1px solid #6B6C72",
                      borderBottom: "1px solid #6B6C72",
                    }}
                  >
                    Category
                  </th>
                  <th colSpan={3} style={{ borderBottom: "1px solid #6B6C72" }}>
                    Offensive
                  </th>
                  <th
                    colSpan={3}
                    style={{
                      borderTopRightRadius: "24px",
                      borderBottom: "1px solid #6B6C72",
                    }}
                  >
                    Defensive
                  </th>
                </tr>
              </thead>
              {Object.keys(activeRegressionType["EFF"])?.length > 0 ? (
                <>
                  <thead>
                    <tr>
                      <th
                        style={{
                          borderTopLeftRadius: "0px",
                          borderRight: "1px solid #6B6C72",
                        }}
                      >
                        Overall
                      </th>
                      <th style={{ borderRight: "1px solid #6B6C72" }}>Diff</th>
                      <th style={{ borderRight: "1px solid #6B6C72" }}>PPP</th>
                      <th style={{ borderRight: "1px solid #6B6C72" }}>SQ PPP</th>
                      <th style={{ borderRight: "1px solid #6B6C72" }}>Diff</th>
                      <th style={{ borderRight: "1px solid #6B6C72" }}>PPP</th>
                      <th
                        style={{
                          borderTopRightRadius: "0px",
                          borderRight: "1px solid #6B6C72",
                        }}
                      >
                        SQ PPP
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        style={{
                          color: "#434343",
                          fontWeight: "bold",
                          borderBottomLeftRadius: "0px",
                        }}
                      >
                        Efficiency
                      </td>
                      <td>
                        <span>{parseFloat(activeRegressionType["EFF"]?.["off_diff"]).toFixed(2)}</span>

                        {parseFloat(activeRegressionType["EFF"]?.["off_diff"]).toFixed(2) == 0 ? null : parseFloat(
                            activeRegressionType["EFF"]?.["off_diff"]
                          ) > 0 ? (
                          <AiOutlineArrowUp color="#208502" className="arrow up" />
                        ) : (
                          <AiOutlineArrowDown color="#D82011" className="arrow down" />
                        )}
                      </td>
                      <td>{parseFloat(activeRegressionType["EFF"]?.["ortg"]).toFixed(2) ?? "N/A"}</td>
                      <td>{parseFloat(activeRegressionType["EFF"]?.["sq_ortg"]).toFixed(2) ?? "N/A"}</td>
                      <td>
                        <span>{parseFloat(activeRegressionType["EFF"]?.["def_diff"]).toFixed(2)}</span>

                        {parseFloat(activeRegressionType["EFF"]?.["def_diff"]).toFixed(2) == 0 ? null : parseFloat(
                            activeRegressionType["EFF"]?.["def_diff"]
                          ) > 0 ? (
                          <AiOutlineArrowUp color="#208502" className="arrow up" />
                        ) : (
                          <AiOutlineArrowDown color="#D82011" className="arrow down" />
                        )}
                      </td>

                      <td>{parseFloat(activeRegressionType["EFF"]?.["drtg"]).toFixed(2) ?? "N/A"}</td>
                      <td style={{ borderBottomRightRadius: "0px" }}>
                        {parseFloat(activeRegressionType["EFF"]?.["sq_drtg"]).toFixed(2) ?? "N/A"}
                      </td>
                    </tr>
                  </tbody>
                </>
              ) : null}
              <thead>
                <tr>
                  <th
                    style={{
                      borderTopLeftRadius: "0px",
                      borderRight: "1px solid #6B6C72",
                    }}
                  >
                    ShotTypes
                  </th>
                  <th style={{ borderRight: "1px solid #6B6C72" }}>Diff</th>
                  <th style={{ borderRight: "1px solid #6B6C72" }}>FG%</th>
                  <th style={{ borderRight: "1px solid #6B6C72" }}>SQ FG%</th>
                  <th style={{ borderRight: "1px solid #6B6C72" }}>Diff</th>
                  <th style={{ borderRight: "1px solid #6B6C72" }}>FG%</th>
                  <th
                    style={{
                      borderTopRightRadius: "0px",
                      borderRight: "1px solid #6B6C72",
                    }}
                  >
                    SQ FG%
                  </th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(activeRegressionType["DEF"].length > 0) && Object.keys(activeRegressionType["OFF"])
                  ? regressionAnalysisCategories.map((category, i) => {
                      return (
                        <tr key={`regressionanalysis${category["key"].toLowerCase()}${i + 1}`}>
                          <td style={{ color: "#434343", fontWeight: "bold" }}>{category["label"]}</td>
                          <TableCellRegressionDiff
                            rankValue={activeRegressionType["OFF"]?.[category["keyDiffRank"]]}
                            {...rankConfig}
                            // link={`/regression-stats?columnType=keyDiff&tableType=offensive&sortBy=${category['key']}&year=${year}`}
                            columnType="def"
                          >
                            {Utils.toPercentFloat(activeRegressionType["OFF"]?.[category["keyDiff"]] * -1)}
                          </TableCellRegressionDiff>
                          <td>{Utils.toPercent(activeRegressionType["OFF"]?.[category["keyFG"]]) ?? "N/A"}</td>
                          <td>{Utils.toPercent(activeRegressionType["OFF"]?.[category["keySQ"]]) ?? "N/A"}</td>
                          <TableCellRegressionDiff
                            rankValue={activeRegressionType["DEF"]?.[category["keyDiffRank"]]}
                            {...rankConfig}
                            // link={`/regression-stats?columnType=keyDiff&tableType=defensive&sortBy=${category['key']}&year=${year}`}
                            columnType="def"
                          >
                            {Utils.toPercentFloat(activeRegressionType["DEF"]?.[category["keyDiff"]] * -1)}
                          </TableCellRegressionDiff>

                          <td>{Utils.toPercent(activeRegressionType["DEF"]?.[category["keyFG"]]) ?? "N/A"}</td>
                          <td>{Utils.toPercent(activeRegressionType["DEF"]?.[category["keySQ"]]) ?? "N/A"}</td>
                        </tr>
                      );
                    })
                  : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TeamRegressionAnalysis;
